/**
 * Folder Scanner Utility
 * 
 * Recursively scans a directory for image files using File System Access API
 * Falls back to file input with webkitdirectory if File System Access API is not available
 */

// Type definitions for File System Access API
interface FileSystemDirectoryHandle {
  kind: 'directory';
  name: string;
  values(): AsyncIterableIterator<FileSystemHandle>;
  getFile(name: string): Promise<File>;
}

interface FileSystemFileHandle {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
}

type FileSystemHandle = FileSystemDirectoryHandle | FileSystemFileHandle;

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/tiff',
  'image/bmp',
];

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.tiff', '.tif', '.bmp'];

/**
 * Check if a file is an image based on MIME type or extension
 */
function isImageFile(file: File): boolean {
  if (file.type && IMAGE_MIME_TYPES.includes(file.type)) {
    return true;
  }
  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  return IMAGE_EXTENSIONS.includes(ext);
}

/**
 * Recursively scan directory handle for image files with path tracking
 */
export async function scanDirectoryHandle(
  dirHandle: any, // FileSystemDirectoryHandle - using any to avoid type issues
  files: Array<{ file: File; relativePath: string }> = [],
  basePath: string = ''
): Promise<Array<{ file: File; relativePath: string }>> {
  try {
    for await (const entry of (dirHandle as any).values()) {
      if (entry.kind === 'file') {
        try {
          const file = await entry.getFile();
          if (isImageFile(file)) {
            const relativePath = basePath ? `${basePath}/${file.name}` : file.name;
            files.push({ file, relativePath });
          }
        } catch (error) {
          console.warn(`Failed to read file ${entry.name}:`, error);
        }
      } else if (entry.kind === 'directory') {
        // Recursively scan subdirectories, tracking path
        const newPath = basePath ? `${basePath}/${entry.name}` : entry.name;
        await scanDirectoryHandle(entry, files, newPath);
      }
    }
  } catch (error) {
    console.error('Error scanning directory:', error);
  }
  
  return files;
}

/**
 * Select folder using File System Access API
 */
export async function selectFolderWithFileSystemAPI(): Promise<Array<{ file: File; relativePath: string }>> {
  if (!('showDirectoryPicker' in window)) {
    throw new Error('File System Access API is not supported in this browser');
  }
  
  try {
    const dirHandle = await (window as any).showDirectoryPicker();
    const files = await scanDirectoryHandle(dirHandle, [], dirHandle.name);
    return files;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      // User cancelled
      return [];
    }
    throw error;
  }
}

/**
 * Select folder using file input with webkitdirectory (fallback)
 */
export function selectFolderWithFileInput(): Promise<Array<{ file: File; relativePath: string }>> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.multiple = true;
    input.accept = 'image/*';
    
    input.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      const files = Array.from(target.files || []);
      const imageFiles = files
        .filter(isImageFile)
        .map(file => ({
          file,
          relativePath: (file as any).webkitRelativePath || file.name
        }));
      resolve(imageFiles);
    };
    
    input.oncancel = () => {
      resolve([]);
    };
    
    input.onerror = (error) => {
      reject(error);
    };
    
    input.click();
  });
}

/**
 * Select folder (uses File System Access API if available, falls back to file input)
 * Returns array of { file: File, relativePath: string } objects
 */
export async function selectFolder(): Promise<Array<{ file: File; relativePath: string }>> {
  if ('showDirectoryPicker' in window) {
    try {
      return await selectFolderWithFileSystemAPI();
    } catch (error: any) {
      if (error.message.includes('not supported')) {
        // Fall back to file input
        return await selectFolderWithFileInput();
      }
      // If user cancelled, return empty array
      if (error.name === 'AbortError') {
        return [];
      }
      throw error;
    }
  } else {
    // Fall back to file input
    return await selectFolderWithFileInput();
  }
}

